import type { SentryMetricCounter } from "@remi/enums/sentry-metric-counter";
import type { SentryMetricDistribution } from "@remi/enums/sentry-metric-distribution";
import type { SentryMetricGauge } from "@remi/enums/sentry-metric-gauge";
import { BaseError, RemiErrorDeprecated, SentryError } from "@remi/error";
import {
  reportToSentry,
  reportToSentryMetricCounter,
  reportToSentryMetricDistribution,
  reportToSentryMetricGauge,
  type SentryMetricOptions,
} from "@remi/sentry";
import type { ILogger, RemiLogger as IRemiLogger, LogAttributesInput } from "@remi/types/logger";

export const emptyLogger: ILogger = {
  info: () => {},
  error: () => {},
  warn: () => {},
  debug: () => {},
  child: () => emptyLogger,
};

/**
 * Wrapper class for logger to enforce some standards in our logging
 */
export class RemiLogger implements IRemiLogger {
  constructor(public logger: ILogger) {}

  /**
   * Log an error with optional attributes
   * @param labelOrError either a label string or an Error object
   * @param opts optional error object or attributes
   */
  error(labelOrError: Error): void;
  error(label: string, error: Error): void;
  error(label: string, opts?: { error?: unknown; attributes?: Record<string, unknown> }): void;
  error(labelOrError: string | Error, opts?: Error | { error?: unknown; attributes?: Record<string, unknown> }): void {
    if (labelOrError instanceof RemiErrorDeprecated) {
      const attributes: Record<string, unknown> = {};
      if (labelOrError.attributes) {
        Object.assign(attributes, labelOrError.attributes);
      }
      attributes.originalError = labelOrError.originalError
        ? {
            message: labelOrError.originalError.message,
            stack: labelOrError.originalError.stack,
            kind: "error",
          }
        : undefined;
      attributes.pkg = labelOrError.pkg;
      attributes.path = labelOrError.path;
      this.logger.error({
        error: {
          message: labelOrError.message,
          stack: labelOrError.stack,
          kind: "error",
        },
        attributes,
        message: labelOrError.message,
      });
      reportToSentry(labelOrError);
    } else if (labelOrError instanceof Error) {
      this.logger.error({
        error: {
          message: labelOrError.message,
          stack: labelOrError.stack,
          kind: "error",
        },
        message: labelOrError.message,
      });
      reportToSentry(labelOrError);
    } else if (opts instanceof RemiErrorDeprecated) {
      const attributes: Record<string, unknown> = {};
      if (opts.attributes) {
        Object.assign(attributes, opts.attributes);
      }
      attributes.originalError = opts.originalError
        ? {
            message: opts.originalError.message,
            stack: opts.originalError.stack,
            kind: "error",
          }
        : undefined;
      attributes.pkg = opts.pkg;
      attributes.path = opts.path;
      this.logger.error({
        error: {
          message: opts.message,
          stack: opts.stack,
          kind: "error",
        },
        attributes,
        message: labelOrError,
      });
      reportToSentry(opts);
    } else if (opts instanceof Error) {
      this.logger.error({
        error: {
          message: opts.message,
          stack: opts.stack,
          kind: "error",
        },
        message: labelOrError,
      });
      reportToSentry(opts);
    } else if (opts) {
      const error = opts.error instanceof Error ? opts.error : new RemiErrorDeprecated({ message: labelOrError });
      this.logger.error({
        error: {
          message: error.message,
          stack: error.stack,
          kind: "error",
        },
        attributes: opts.attributes,
        message: labelOrError,
      });
      reportToSentry(error);
    } else {
      this.logger.error({
        error: {
          message: labelOrError,
          stack: new RemiErrorDeprecated({ message: "Replace with appropriate message" }).stack,
          kind: "error",
        },
        message: labelOrError,
      });
      reportToSentry(new RemiErrorDeprecated({ message: labelOrError }));
    }
  }

  /**
   * Capture error for non-blocking error reporting
   * Only accepts an error object, no message parameter or attributes
   * These are stored in the error itself
   */
  captureError(error: Error, extraContext?: Record<string, { [key: string]: unknown }>): Error {
    let shouldReportErrorToSentry = true;

    if (error instanceof SentryError) {
      // Better log for "typed" errors
      const { tags, attributes, fingerprint } = error.getSentryData();

      this.logger.error({
        error: {
          message: error.message,
          stack: error.stack,
          kind: "error",
        },
        attributes: {
          tags,
          data: attributes,
          fingerprint,
        },
        message: `[${error.service}] [${error.errorType}] ${error.message}`,
      });

      if (error.metric) {
        switch (error.metric.type) {
          case "counter":
            reportToSentryMetricCounter(error.metric.name, error.metric.value, {
              attributes: error.metric.attributes,
            });
            break;
          case "gauge":
            reportToSentryMetricGauge(error.metric.name, error.metric.value, {
              attributes: error.metric.attributes,
            });
            break;
          case "distribution":
            reportToSentryMetricDistribution(error.metric.name, error.metric.value, {
              attributes: error.metric.attributes,
            });
            break;
        }
        shouldReportErrorToSentry = false;
      }
    } else {
      // Standard error logging for all other error types
      this.logger.error({
        error: {
          message: error.message,
          stack: error.stack,
          kind: "error",
        },
        message: `[${error.name}] ${error.message}`,
      });
    }

    if (shouldReportErrorToSentry) {
      reportToSentry(error, extraContext);
    }

    // To prevent duplicating sentry errors, once an error has been captured, it gets transformed to a base error so we can skip reporting it in a higher layer
    let baseError: BaseError;
    if (!(error instanceof BaseError)) {
      baseError = new BaseError({ message: error.message, cause: error });
    } else {
      baseError = error;
    }
    baseError.hasBeenReportedToSentry = true;
    return baseError;
  }

  captureCounterMetric(metric: SentryMetricCounter, counter: number = 1, options?: SentryMetricOptions) {
    reportToSentryMetricCounter(metric, counter, options);
  }

  captureGaugeMetric(metric: SentryMetricGauge, value: number, options?: SentryMetricOptions) {
    reportToSentryMetricGauge(metric, value, options);
  }

  captureDistributionMetric(metric: SentryMetricDistribution, value: number, options?: SentryMetricOptions) {
    reportToSentryMetricDistribution(metric, value, options);
  }

  cleanAttributes(attributes: Record<string, unknown>) {
    if ("message" in attributes) {
      return {
        ...attributes,
        message: undefined,
        _message: attributes.message,
      };
    }
    return attributes;
  }

  info<T>(message: string, attributes?: LogAttributesInput<T>): void;
  info(message: string, attributes: Record<string, unknown> = {}): void {
    const cleaned = this.cleanAttributes(attributes);
    this.logger.info({ ...cleaned, message });
  }

  warn<T>(message: string, attributes?: LogAttributesInput<T>): void;
  warn(message: string, attributes: Record<string, unknown> = {}): void {
    const cleaned = this.cleanAttributes(attributes);
    this.logger.warn({ ...cleaned, message });
  }

  /**
   * Used for debug logging
   * These will only show in development and will not be sent to prod logging systems
   */
  debug(message: string, attributes: Record<string, unknown> = {}) {
    attributes = this.cleanAttributes(attributes);
    this.logger.debug({ ...attributes, message });
  }

  /**
   * Used for copying the logger to a new context
   */
  child(attributes: Record<string, unknown>) {
    const childLogger = new RemiLogger(this.logger.child(attributes));
    return childLogger;
  }

  /**
   * Used for adding attributes to the logger
   */
  addGlobalAttributes(attributes: Record<string, unknown>) {
    this.logger = this.logger.child(attributes);
  }

  /**
   * Uses a stack trace to get the file name of the calling module
   */
  getFileNameFromStackTrace() {
    const e = new RemiErrorDeprecated({ message: "Replace with appropriate message" });
    const stack = e.stack?.split("\n")[3];
    const match = stack?.match(/\(?(.+?):\d+:\d+\)?/);
    return match?.[1]?.split("/").pop() ?? "unknown";
  }
}
